import { Injectable } from '@angular/core';

import { Socket } from 'socket.io-client';
import {
  Observable,
  BehaviorSubject,
  filter,
  share,
  tap,
  from,
  switchMap,
  map,
  Subject,
} from 'rxjs';

import { NestedEvent, WebSocketEventsEnum } from '@shared/websocket';

@Injectable()
export abstract class BaseSocketService {
  protected readonly socketSubject = new BehaviorSubject<Socket>(null);
  // socket channels cache
  private readonly events$ = new Subject<[WebSocketEventsEnum, any[]]>();
  private readonly events = this.events$
    .pipe(
      switchMap(([event, data]) => {
        if (event === WebSocketEventsEnum.events) {
          return from(data as NestedEvent[]).pipe(
            map(({event, data}) => ([event, data])),
          );
        }

        return from([[event, data]]);
      }),
      share(),
    );

  // socket creation logic
  protected abstract createSocket(): Observable<Socket>;

  getEventStream<T = any>(event: string): Observable<T> {
    return this.events.pipe(
      filter(([_event]) => _event === event),
      map(([, data]) => data),
    );
  }

  connect(): void {
    const socket = this.socketSubject.getValue();
    if (!socket) {
      this.createSocket()
        .pipe(tap(socket => {
          socket.onAny((event, data) => this.events$.next([event, data]));
          this.socketSubject.next(socket);
        }))
        .subscribe();
    }
  }

  disconnect(): void {
    const socket = this.socketSubject.getValue();
    if (socket) {
      socket.disconnect();
    }
    this.socketSubject.next(null);
  }
}
